import React, { useState, useEffect, useCallback, forwardRef, useRef, useImperativeHandle, Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import { Grid, Typography, FormControl, makeStyles, } from '@material-ui/core';
import { TextField, Select, TextFieldDatePicker, HelperText, CheckBox, CircularLoader } from '@components/atoms';
import { DragNDrop, DialogAlert, DialogConfirm } from '@components/molecules';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

//API Requests
import { CredentialsService } from "@services";

import { setManufacturers } from '@store/addCertificate';
import certificates from 'src/store/certificates';

const useStyles = makeStyles(theme => ({
    imageWrapper: {
        marginTop: theme.spacing(3),
    },
    typography: {
        color: theme.palette.primary.main,
    },
    dialog: {

    }
}));

const CERT_STATUS_OPTIONS = [
    {
        label: 'Active',
        value: 'Active'
    },
    {
        label: 'Inactive',
        value: 'Inactive'
    },
];

const CovidVaccine = forwardRef((props, ref) => {
    const { filterValues, isSmUp, hash, formik, formik: { values }, state, setState, manufacturersList, certificateList, actions: { setManufacturers }, addCertificate, setECardData, isChangingImage, setIsChangingImage } = props;
    const [vaccineState, setVaccineState] = useState({ loading: true, openBoosterAlert: false, openUnVaccinatedConfirm: false, boosterAlertMsg: '', boosterShotAlertShownOnce: false });
    const classes = useStyles();
    const previousCertificateRef = useRef();

    useEffect(() => {
        if (addCertificate) setIsChangingImage(true);
        if (!values.certificateID || !Object.values(values).join('').length) return;
        if (previousCertificateRef.current && Number(previousCertificateRef.current) !== Number(values.certificateID)) {
            ['documentNumber', 'issueDate', 'frontImage'].forEach(v => formik.setFieldValue(v, '', false));
            setECardData();
        }
        previousCertificateRef.current = values.certificateID;
        const selectedCertificate = filterValues.certificateTypes.find(v => Number(values.certificateID) === Number(v.certificateID));
        setState(prevState => ({ ...prevState, selectedCertificate }));
    }, [values]);

    const getManufacturersList = async () => {
        const manufacturersList = await CredentialsService.getManufacturersList();
        setManufacturers(manufacturersList);
        createAndSetManufacturerOptions(manufacturersList);
    };

    const createAndSetManufacturerOptions = (manufacturersList) => {
        const manufacturersOptions = manufacturersList.map(v => ({ label: v.manufacturer, value: _.toLower(v.manufacturer) }));
        setVaccineState(prevState => ({ ...prevState, manufacturersOptions, loading: false }));
    };

    useEffect(() => {
        try {
            if (!manufacturersList) {
                setVaccineState(prevState => ({ ...prevState, loading: true }));
                getManufacturersList();
            } else {
                createAndSetManufacturerOptions(manufacturersList);
            }
        } catch (error) {
            setVaccineState(prevState => ({ ...prevState, loading: false }));
        }
    }, []);

    const onCertificateNameChange = (event) => {
        formik.setFieldValue('issueDate', '');
        formik.setFieldValue('manufacturer', '');
        formik.setFieldValue('lotNumber', '');
        formik.setFieldValue('isVaccineDeclined', false);
        formik.setFieldValue('vaccineBooster', false);
        formik.setFieldValue('frontImage', '');
        formik.handleChange(event);
    };

    const onDialogToggle = (dialogState) => {
        setVaccineState(prevState => ({ ...prevState, [dialogState]: !prevState[dialogState] }));
    };

    const onUnVaccinatedChange = (event) => {
        formik.handleChange(event);
        if (event.target.checked) onDialogToggle('openUnVaccinatedConfirm');
    };

    const handleChangeImage = () => {
        formik.setFieldValue('certificateImage', '');
        // setIsChangingImage(true);
    };

    const onOkUnVaccinatedConfirmClickHandler = () => {
        formik.setFieldValue('issueDate', moment().format('YYYY-MM-DD'), false);
        formik.setFieldValue('manufacturer', '', false);
        formik.setFieldValue('lotNumber', '', false);
        formik.setFieldValue('frontImage', '', false);
        onDialogToggle('openUnVaccinatedConfirm');
    }

    const onCancelUnVaccinateConfirmClickHandler = () => {
        onDialogToggle('openUnVaccinatedConfirm');
        formik.setFieldValue('isVaccineDeclined', false);
    };

    const handleBoosterShootChange = (event, confirmBtnClicked) => {
        if (!confirmBtnClicked) formik.handleChange(event);
        const vaccineCount = getvaccineCount();
        const boosterAlertMsg =
            vaccineCount.johnson >= 1 || (vaccineCount.moderna + vaccineCount.pfizer + vaccineCount.astraZeneca) >= 2
                ? 'Based on the COVID Vaccines you have added so far it seems this could be a booster, is that correct?'
                : 'You are adding this vaccine as a COVID booster vaccine, would you like to continue with this selection?';
        setVaccineState(prevState => ({ ...prevState, boosterAlertMsg }));
    };

    useImperativeHandle(
        ref,
        () => ({
            validateAndDisplayBoosterAlert: () => {
                const { issueDate, vaccineBooster, isVaccineDeclined, activeFlag } = formik.values;
                const vaccineCount = getvaccineCount();
                const isBooster = moment(issueDate).isAfter('2021/07/31');

                if (!vaccineBooster && !vaccineState.boosterShotAlertShownOnce && !isVaccineDeclined && (activeFlag == 'Active' || activeFlag == 'A')) {
                    if (vaccineCount.johnson >= 1 && isBooster) {
                        handleBoosterShootChange(null, true);
                        setVaccineState(prevState => ({ ...prevState, openBoosterAlert: true }));
                        return false;
                    }
                    if (vaccineCount.moderna + vaccineCount.pfizer + vaccineCount.astraZeneca >= 2 && isBooster) {
                        handleBoosterShootChange(null, true);
                        setVaccineState(prevState => ({ ...prevState, openBoosterAlert: true }));
                        return false;
                    }
                }
                return true;
            }
        }));

    const onYesBoosterAlertClickHandler = () => {
        formik.setFieldValue('vaccineBooster', true);
        setVaccineState(prevState => ({ ...prevState, boosterShotAlertShownOnce: true }));
        onDialogToggle('openBoosterAlert');
    };

    const onNoBoosterAlertClickHandler = () => {
        setVaccineState(prevState => ({ ...prevState, boosterShotAlertShownOnce: true }));
        onDialogToggle('openBoosterAlert');
    };

    const getvaccineCount = useCallback(() => {
        if (certificateList !== null) {
            const vaccineCount = certificateList.reduce(
                (result, v) => {
                    if (Number(v.certificateID) === 1007) {
                        if (/^johnson & johnson$/i.test(v.manufacturer)) {
                            result.johnson++;
                        } else if (/^moderna$/i.test(v.manufacturer)) {
                            result.moderna++;
                        } else if (/^pfizer$/i.test(v.manufacturer)) {
                            result.pfizer++;
                        } else if (/^astraZeneca$/i.test(v.manufacturer)) {
                            result.astraZeneca++;
                        }
                    }
                    return result;
                },
                { johnson: 0, moderna: 0, pfizer: 0, astraZeneca: 0, }
            );
            return vaccineCount;
        } else {
            return { johnson: 0, moderna: 0, pfizer: 0, astraZeneca: 0, };
        }
    }, [certificateList]);

    const renderVaccineBooster = () => {
        const vaccineCount = getvaccineCount();
        const vaccineCondition = !(vaccineCount.johnson >= 1 || vaccineCount.moderna >= 2 || vaccineCount.pfizer >= 2 || vaccineCount.astraZeneca >= 2);
        const issueDateDateCondition = moment(formik.values.issueDate).isBefore('2021/08/01');
        const hideBoosterShot = !formik.values.issueDate || formik.values.isVaccineDeclined || issueDateDateCondition || vaccineCondition;
        if (hideBoosterShot) return null;
        return (
            <CheckBox
                {...{
                    name: 'vaccineBooster',
                    label: 'Vaccine Booster',
                    onChange: (event) => handleBoosterShootChange(event),
                    formProps: { required: true, fullWidth: true, },
                    checked: formik.values.vaccineBooster,
                    error: formik.touched.vaccineBooster && Boolean(formik.errors.vaccineBooster),
                    helperText: formik.touched.vaccineBooster && formik.errors.vaccineBooster,
                    color: "primary"
                }} />
        );
    };

    const renderVaccineFields = () => {
        return (
            <Fragment>
                <Select
                    {...{
                        name: 'certificateID',
                        label: 'Certificate Name',
                        onChange: onCertificateNameChange, //formik.handleChange,
                        onBlur: formik.handleBlur,
                        options: state.certificateTypesOptions || [],
                        formProps: { variant: "outlined", required: true, fullWidth: true, },
                        value: formik.values.certificateID,
                        error: formik.touched.certificateID && Boolean(formik.errors.certificateID),
                        helperText: formik.touched.certificateID && formik.errors.certificateID,
                        disabled: !addCertificate
                    }} />
                <TextFieldDatePicker
                    {...{
                        name: 'issueDate',
                        label: 'Dose',
                        required: addCertificate,
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        fullWidth: true,
                        variant: "outlined",
                        style: { width: '15rem' },
                        value: formik.values.issueDate,
                        error: formik.touched.issueDate && Boolean(formik.errors.issueDate),
                        helperText: formik.touched.issueDate && formik.errors.issueDate,
                        disabled: formik.values.isVaccineDeclined
                    }} />
                <Select
                    {...{
                        name: 'manufacturer',
                        label: 'Manufacturer',
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        options: vaccineState.manufacturersOptions || [],
                        formProps: { variant: "outlined", required: !formik.values.isVaccineDeclined, fullWidth: true, },
                        value: formik.values.manufacturer,
                        error: formik.touched.manufacturer && Boolean(formik.errors.manufacturer),
                        helperText: formik.touched.manufacturer && formik.errors.manufacturer,
                        disabled: formik.values.isVaccineDeclined,
                    }} />
                <TextField
                    {...{
                        name: 'lotNumber',
                        label: 'Lot Number',
                        isCapital: true,
                        onChange: formik.handleChange,
                        onBlur: formik.handleBlur,
                        fullWidth: true,
                        // required: !formik.values.isVaccineDeclined,
                        value: formik.values.lotNumber,
                        error: formik.touched.lotNumber && Boolean(formik.errors.lotNumber),
                        helperText: formik.touched.lotNumber && formik.errors.lotNumber,
                        disabled: formik.values.isVaccineDeclined,
                    }} />
                {((!formik.values.manufacturer && !formik.values.lotNumber && !formik.values.frontImage) || (!addCertificate && !values.certificateImage)) &&
                    (<CheckBox
                        {...{
                            name: 'isVaccineDeclined',
                            label: 'Unvaccinated',
                            onChange: onUnVaccinatedChange, //formik.handleChange,
                            formProps: { required: true, fullWidth: true, },
                            checked: formik.values.isVaccineDeclined,
                            error: formik.touched.isVaccineDeclined && Boolean(formik.errors.isVaccineDeclined),
                            helperText: formik.touched.isVaccineDeclined && formik.errors.isVaccineDeclined,
                            color: "primary"
                        }} />)
                }
                {renderVaccineBooster()}
            </Fragment>
        );
    };

    const renderImageUploadFields = () => {
        return (
            <Fragment>
                {
                    !addCertificate && values.certificateImage && !isChangingImage && (
                        <Grid item className={classes.imageWrapper}>
                            <FormControl className={classes.formControl} error={formik.touched.certificateImage && Boolean(formik.errors.certificateImage)}>
                                <Typography variant="h6" gutterBottom className={classes.typography}>
                                    Credential Image Upload
                                </Typography>
                                <DragNDrop {...{
                                    name: 'certificateImage',
                                    uploadedImage: formik.values.certificateImage,
                                    hasUploadedImage: !!formik.values.certificateImage,
                                    type: 'png, jpeg, jpg',
                                    changeImageClick: () => handleChangeImage(),
                                    restrictFileExplorer: true,
                                }} />
                                <HelperText {...{
                                    error: formik.touched.certificateImage && Boolean(formik.errors.certificateImage),
                                    helperText: formik.errors.certificateImage,
                                }} />
                            </FormControl>
                        </Grid>
                    )
                }
                {
                    state.selectedCertificate && !values.certificateImage && state.selectedCertificate.isFrontImageRequired === "True" && (
                        <Grid item className={classes.imageWrapper}>
                            <FormControl className={classes.formControl} error={Boolean(formik.errors.frontImage)}>
                                <Typography variant="h6" gutterBottom className={classes.typography}>
                                    Front Image Upload
                                </Typography>
                                <DragNDrop {...{
                                    name: 'frontImage',
                                    uploadedImage: formik.values.frontImage,
                                    hasUploadedImage: !!formik.values.frontImage,
                                    setUploadedImage: data => formik.setFieldValue('frontImage', data),
                                    type: 'png, jpeg, jpg',
                                    dropZoneProps: { disabled: formik.values.isVaccineDeclined },
                                }} />
                                <HelperText {...{
                                    error: Boolean(formik.errors.frontImage),
                                    helperText: formik.errors.frontImage,
                                }} />
                            </FormControl>
                        </Grid>
                    )
                }
            </Fragment>
        );
    };
    if (vaccineState.loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularLoader /></div>;
    return (
        <Fragment>
            <Grid item xs={12} md={5}>
                {isSmUp || hash.split('-').pop() === '1' ? renderVaccineFields() : null}
            </Grid>
            <Grid item xs={12} md={6}>
                {isSmUp || hash.split('-').pop() > '1' ? renderImageUploadFields() : null}
            </Grid>
            <DialogAlert {...{ dialogProps: { open: vaccineState.openBoosterAlert, classes: { paper: classes.dialog } }, title: vaccineState.boosterAlertMsg, onYesClickHandler: onYesBoosterAlertClickHandler, onNoClickHandler: onNoBoosterAlertClickHandler }} />
            <DialogConfirm {...{ dialogProps: { open: vaccineState.openUnVaccinatedConfirm, classes: { paper: classes.dialog } }, title: 'All fields will be cleared. Do you wish to Continue?', onOkClickHandler: onOkUnVaccinatedConfirmClickHandler, onCancelClickHandler: onCancelUnVaccinateConfirmClickHandler }} />
        </Fragment>
    );
});

const mapStateToProps = (state) => {
    return {
        manufacturersList: state.addCertificate.manufacturersList,
        certificateList: state.certificates.certificates,
    }
};

const mapDispatchToProps = dispatch => ({
    dispatch,
    actions: bindActionCreators({ setManufacturers }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(CovidVaccine);